/* React */
import React, { useState } from 'react';

/* Translation */
import { useTranslation } from 'react-i18next';

/* Components */
import TeacherItem from './TeacherItem';

/* Styled Components */
import {
  List,
  ListHeader,
  ListName,
  ListAssignedClasses,
  ListRole,
  ListEmail,
  StylesImageModalResultsDeleteTeacher,
} from './styles/TeacherList';

import { EmptyList } from '../sharedComponents/styles/EmptyList';

import Dialog from 'components/dialog/Dialog';
import useTeacher from 'hooks/useTeacher';
import { useRouter } from 'routes';

import memberAddedErrorImage from 'images/error.svg';
import memberAddedSuccessImage from 'images/checked.svg';
import RendererIf from 'components/RendererIf';
import ResultActionModal from 'components/shared/ResultActionModal/ResultActionModal';
import { TooltipSortArrow } from '../SortArrow';

/* Services, Hooks, Constants */

interface TeacherListProps {
  teachers?: any;
  handleOnFinishDeleteTeacherFlow?: any;
  selectedAll?: boolean;
  selectedTeachers?: any;
  toggleSelectTeacher?(teacher: any): void;
}

const TeacherList: React.FC<TeacherListProps> = ({
  teachers,
  handleOnFinishDeleteTeacherFlow,
  selectedTeachers,
  toggleSelectTeacher,
}) => {
  const { t } = useTranslation();
  const [alphaSort, setAlphaSort] = useState(false);

  const { deleteTeacher } = useTeacher();
  const { currentParamsRef } = useRouter();

  /* open modals */
  const [idTeacherToDelete, setIdTeacherToDelete] = useState<any>(false);

  const {
    current: { centerId },
  } = currentParamsRef;

  const [showResultActionModal, setShowResultActionModal] = useState<any>(
    false,
  );

  const ConfigResultActionModal = {
    success: {
      title: t('common:SUCCESS'),
      image: memberAddedSuccessImage,
      configPopsModal: {
        showCloseButton: false,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
    error: {
      title: t('settings:ERROR'),
      image: memberAddedErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
  };

  const [statusResultActionModal, setStatusResultActionModal] = useState<any>(
    ConfigResultActionModal.error,
  );

  /* DELETE TEACHER functions */
  const handleDeleteFlow = (teacherId: number) => {
    setIdTeacherToDelete(teacherId);
  };

  const handleDeleteTeacher = () => {
    deleteTeacher(centerId, idTeacherToDelete)
      .then((response: any) => {
        setStatusResultActionModal(ConfigResultActionModal.success);
        if (handleOnFinishDeleteTeacherFlow) handleOnFinishDeleteTeacherFlow();
      })
      .catch(() => {
        setStatusResultActionModal(ConfigResultActionModal.error);
      })
      .finally(() => {
        setIdTeacherToDelete(false);
        setShowResultActionModal(true);
      });
  };

  /* SORT teacher list */
  const renderTeachers = (list: any) => {
    return list.map((item: any, index: any) => {
      return (
        <TeacherItem
          selected={selectedTeachers?.find(
            (teacher: any) => teacher.id === item.id,
          )}
          toggleSelectTeacher={toggleSelectTeacher}
          key={index}
          teacher={item}
          handleDeleteTeacher={handleDeleteFlow}
        />
      );
    });
  };

  return (
    <List>
      <ListHeader>
        <ListName>
          {t('settings:TEACHER_NAME')}
          <TooltipSortArrow sorted={alphaSort} onSort={setAlphaSort} />
        </ListName>
        <ListAssignedClasses>
          {t('settings:ASSIGNED_CLASSES')}
        </ListAssignedClasses>
        <ListRole>{t('common:ROLE')}</ListRole>
        <ListEmail>{t('common:EMAIL')}</ListEmail>
      </ListHeader>

      <RendererIf
        condition={teachers.length !== 0}
        alternative={
          <EmptyList>{t('settings:CENTER_SECTION.TEACHERS_EMPTY')}</EmptyList>
        }
      >
        {renderTeachers(alphaSort ? teachers.reverse() : teachers)}
      </RendererIf>

      <Dialog
        mainText={t('settings:DELETE')}
        cancelText={t('common:CANCEL')}
        title={t('settings:CENTER_SECTION.TEACHER_DELETE.MODAL_CONFIRM.TITLE')}
        show={idTeacherToDelete}
        onMainAction={handleDeleteTeacher}
        onDialogClose={() => setIdTeacherToDelete(false)}
        onCancelAction={() => setIdTeacherToDelete(false)}
      >
        <>{t('settings:CENTER_SECTION.TEACHER_DELETE.MODAL_CONFIRM.TEXT')}</>
      </Dialog>
      <RendererIf condition={showResultActionModal}>
        <ResultActionModal
          styles={{ Image: StylesImageModalResultsDeleteTeacher }}
          {...statusResultActionModal}
        />
      </RendererIf>
    </List>
  );
};

export default TeacherList;
